import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { startCase } from "lodash";

import useFolderTabs from "@shared/v2/Modal/useFolderTabs";
import { Spinner } from "@shared/v2/Icomoon";
import Button from "@shared/v2/Button";
import ErrorAlert from "@shared/UtilsV1/ErrorAlert";
import Modal from "@shared/v2/Modal";
import TextButton from "@shared/v2/TextButton";
import FolderTab from "@shared/UtilsV1/FolderTab";
import GeneralView from "./ModalViews/GeneralView";
import UserDetailsView from "./ModalViews/UserDetailsView";
import ContactInfoView from "./ModalViews/ContactInfoView";
import PersonalDetailsView from "./ModalViews/PersonalDetailsView";
import CustomDatesView from "./ModalViews/CustomDatesView";
import WebsiteInfoView from "./ModalViews/WebsiteInfoView";
import AccountSettingsView from "./ModalViews/AccountSettingsView";
import usePersonDetailsModal from "./hooks/usePersonDetailsModal";
import PdpLink from "./PdpLink";
import PhoneNumberModal from "./PhoneNumberModal";
import AddSubdomainModal from "./ModalViews/AddSubdomainModal";

import { setPerson, setPersonValue } from "../reducers/contactsReducer";
import EditSubdomainModal from "./ModalViews/EditSubdomainModal";
import RemoveSubdomainModal from "./ModalViews/RemoveSubdomainModal";
import ImportPersonSearch from "./ModalViews/ImportPersonSearch";
import { transformPerson } from "../reducers/contactsReducer/helpers";
import { useOriginalContactPerson, useHasSocialMediaErrors } from "../reducers/contactsReducer/selectors";

const WEBSITE_INFO_REGEX = /^(?:user|websiteProfile|lender)$/;

const PersonDetailsModal = ({
  containerClassName = "",
  modalType = "lead",
  onClose = () => {},
  person = null,
  personId = null,
  prefilledValues = null,
  show,
  showAddDetailsButton = true,
}) => {
  const [addSubDomainOpen, setAddSubDomainOpen] = useState(false);
  const [editSubDomainOpen, setEditSubDomainOpen] = useState(false);
  const [removeSubDomainOpen, setRemoveSubDomainOpen] = useState(false);
  const dispatch = useDispatch();
  const isEditing = Boolean(person?.id || personId);
  const typeName = modalType === "lead" ? "Contact" : startCase(modalType);
  const title = isEditing ? `Edit ${typeName} Details` : `Add ${typeName}`;
  const originalPerson = useOriginalContactPerson();
  const hasSocialMediaErrors = useHasSocialMediaErrors();
  const { actions, errors, formLoading, showPhoneContext, loading } = usePersonDetailsModal({
    modalType,
    person,
    personId,
    prefilledValues,
    show,
  });

  const folderTabs = useMemo(
    () => [
      { tabName: "General", show: true },
      { tabName: "User Details", show: modalType === "user" },
      { tabName: "Contact Info", show: true },
      { tabName: "Personal Details", show: true },
      {
        tabName: "Custom Dates",
        show: (isEditing && !["lender", "websiteProfile"].includes(modalType)) || modalType === "user",
      },
      {
        tabName: "Website Info",
        show:
          WEBSITE_INFO_REGEX.test(modalType) ||
          originalPerson.websiteRosterPersonAgent === "true" ||
          originalPerson.websiteRosterPreferredLender === "true",
      },
      { tabName: "Account Settings", show: modalType === "user" },
    ],
    [modalType, isEditing, originalPerson],
  );

  const { tabs, activeTabName, onScroll, refs } = useFolderTabs(folderTabs);

  return (
    <>
      {modalType === "user" && (
        <>
          <AddSubdomainModal
            show={addSubDomainOpen}
            onClose={() => setAddSubDomainOpen(false)}
            onSave={(value) => {
              dispatch(setPersonValue({ key: "loginUserAttributes.subdomain", value }));
            }}
          />
          <EditSubdomainModal show={editSubDomainOpen} onClose={() => setEditSubDomainOpen(false)} />
          <RemoveSubdomainModal show={removeSubDomainOpen} onClose={() => setRemoveSubDomainOpen(false)} />
        </>
      )}
      <Modal
        className="tw-flex tw-items-center tw-justify-center"
        contentClassName="tw-max-w-[850px] tw-w-[100vw] tw-max-h-[85vh] tw-flex tw-flex-col tw-gap-[32px]"
        show={show}
        onHide={onClose}
        containerClassName={containerClassName}
      >
        <Modal.Header
          className="tw-relative"
          title={title}
          description={
            modalType === "lead"
              ? "Fill out this basic info in order to keep in touch with this contact through your CRM."
              : ""
          }
          closeButton
        >
          <PdpLink personId={personId} />
        </Modal.Header>

        <Modal.Body className="tw-relative tw-flex tw-flex-col tw-overflow-hidden">
          {errors?.length > 0 && (
            <div className="tw-flex-1 tw-min-h-[72px] tw-max-h-[240px] tw-overflow-auto tw-mb-[8px]">
              <ErrorAlert errorMessages={errors} onClose={actions.clearErrors} />
            </div>
          )}

          {formLoading && (
            <div className="tw-flex tw-w-full tw-justify-center">
              <Spinner size="xxxl" className="tw-animate-spin" />
            </div>
          )}

          {!formLoading && (
            <div className="tw-flex tw-overflow-hidden">
              <div className="tw-w-[160px] tw-text-14px tw-text-neutral-gray-75 tw-border-r tw-border-solid tw-border-0 tw-border-neutral-gray-15 ">
                {tabs.map((tab) => (
                  <FolderTab
                    key={tab.tabName}
                    tabName={tab.tabName}
                    isActive={activeTabName === tab.tabName}
                    onClick={() => tab.ref.current.scrollIntoView({ behavior: "smooth" })}
                  />
                ))}
              </div>
              <div className="tw-flex-1 tw-overflow-auto" onScroll={onScroll}>
                <div className="tw-px-[24px] tw-mr-[12px]">
                  <hr className="tw-w-full tw-border-gray-10 tw-m-0" />
                </div>

                {!person?.id && !personId && (modalType === "websiteProfile" || modalType === "lender") && (
                  <div className="tw-px-[24px] tw-pt-[24px]">
                    <ImportPersonSearch
                      modalType={modalType}
                      onChange={(p) =>
                        dispatch(
                          setPerson({
                            originalPerson: transformPerson(p),
                            person: transformPerson({
                              ...p,
                              ...(modalType === "websiteProfile" && { websiteRosterPersonAgent: "true" }),
                              ...(modalType === "lender" && { websiteRosterPreferredLender: "true" }),
                            }),
                          }),
                        )
                      }
                    />
                  </div>
                )}

                <div className="tw-relative tw-flex-auto tw-flex tw-flex-col tw-p-[24px] tw-gap-[24px] tw-overflow-y-auto scrollbar">
                  <div ref={refs.General}>
                    <GeneralView modalType={modalType} />
                  </div>

                  {modalType === "user" && (
                    <>
                      <hr className="tw-w-full tw-border-gray-10" />
                      <div ref={refs["User Details"]}>
                        <UserDetailsView />
                      </div>
                    </>
                  )}

                  <hr className="tw-w-full tw-border-gray-10" />
                  <div ref={refs["Contact Info"]}>
                    <ContactInfoView modalType={modalType} />
                  </div>

                  <hr className="tw-w-full tw-border-gray-10" />
                  <div ref={refs["Personal Details"]}>
                    <PersonalDetailsView
                      modalType={modalType}
                      birthdateError={errors
                        ?.find((e) => e.startsWith("Birthdate"))
                        ?.replace("Birthdate: ", "")}
                    />
                  </div>

                  {((isEditing && !["lender", "websiteProfile"].includes(modalType)) ||
                    modalType === "user") && (
                    <>
                      <hr className="tw-w-full tw-border-gray-10" />
                      <div ref={refs["Custom Dates"]}>
                        <CustomDatesView />
                      </div>
                    </>
                  )}

                  {folderTabs.find((t) => t.tabName === "Website Info").show && (
                    <>
                      <hr className="tw-w-full tw-border-gray-10" />
                      <div ref={refs["Website Info"]}>
                        <WebsiteInfoView
                          modalType={modalType}
                          onSubdomainOpen={() => setAddSubDomainOpen(true)}
                          onSubdomainEdit={() => setEditSubDomainOpen(true)}
                          onSubdomainRemove={() => setRemoveSubDomainOpen(true)}
                        />
                      </div>
                    </>
                  )}
                  {modalType === "user" && (
                    <>
                      <hr className="tw-w-full tw-border-gray-10" />
                      <div ref={refs["Account Settings"]}>
                        <AccountSettingsView modalType={modalType} isEditing={isEditing} />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="tw-flex tw-justify-between tw-items-center">
          <Button
            onClick={() => {
              actions.clearErrors();
              onClose();
            }}
            data-cy="ec-modal-cancel-btn"
            schema="tertiary"
            size="medium"
          >
            Cancel
          </Button>

          <div className="tw-flex tw-items-center tw-gap-[16px]">
            {showAddDetailsButton && modalType === "lead" && !personId && (
              <TextButton
                size="medium"
                disabled={formLoading || loading || hasSocialMediaErrors}
                onClick={() =>
                  actions.upsertPerson({ modalType }).then(({ payload }) => {
                    if (payload.slug) window.location.assign(`/people/${payload.slug}`);
                  })
                }
              >
                Add More Details
              </TextButton>
            )}

            {personId && (
              <TextButton
                size="medium"
                disabled={formLoading || loading || hasSocialMediaErrors}
                onClick={() => actions.upsertPerson({ modalType })}
              >
                Save Changes
              </TextButton>
            )}

            <Button
              className="tw-flex tw-items-center"
              size="medium"
              isLoading={formLoading || loading}
              disabled={hasSocialMediaErrors}
              onClick={() =>
                actions.upsertPerson({ modalType }).then(({ meta, payload }) => {
                  if (meta.requestStatus === "fulfilled") onClose(payload);
                })
              }
            >
              Save & Close
            </Button>
          </div>
        </Modal.Footer>
        <PhoneNumberModal
          showContext={showPhoneContext}
          onClose={(phoneRemovalAck) => {
            if (phoneRemovalAck) {
              actions.setPersonValue({ key: "phoneRemovalAck", value: true });
              actions.upsertPerson({ modalType }).then(({ meta, payload }) => {
                if (meta.requestStatus === "fulfilled") onClose(payload);
              });
            }
            actions.setShowPhoneContext(null);
          }}
        />
      </Modal>
    </>
  );
};

PersonDetailsModal.propTypes = {
  containerClassName: PropTypes.string,
  modalType: PropTypes.oneOf(["lead", "user", "websiteProfile", "lender"]),
  onClose: PropTypes.func,
  person: PropTypes.shape(),
  personId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  prefilledValues: PropTypes.shape(),
  show: PropTypes.bool.isRequired,
  showAddDetailsButton: PropTypes.bool,
};

export default PersonDetailsModal;
